import React from "react";
import {
  Box,
  IconButton,
  Paper,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";

export const MANAGEMENT_ROWS_PER_PAGE = 20;

export const MANAGEMENT_TABLE_SX = {
  width: "100%",
  tableLayout: "fixed",
  fontSize: 14,
};

export const MANAGEMENT_HEADER_CELL_SX = {
  bgcolor: "action.hover",
  color: "text.secondary",
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "0.02em",
  py: 1.25,
  whiteSpace: "nowrap",
};

export const MANAGEMENT_BODY_CELL_SX = {
  fontSize: 14,
  py: 1,
  verticalAlign: "middle",
};

export const MANAGEMENT_ACTION_CELL_SX = {
  ...MANAGEMENT_BODY_CELL_SX,
  width: 72,
  px: 1,
  textAlign: "center",
};

export const MANAGEMENT_SPACER_HEADER_CELL_SX = {
  ...MANAGEMENT_HEADER_CELL_SX,
  p: 0,
};

export const MANAGEMENT_SPACER_BODY_CELL_SX = {
  ...MANAGEMENT_BODY_CELL_SX,
  p: 0,
};

const truncatedTextSx = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export function ManagementListPage({ title, count, action, toolbar, children }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 1440, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
          <Typography component="h1" variant="h5" sx={{ fontSize: 24, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            全{count}件
          </Typography>
        </Box>
        {action}
      </Box>
      {toolbar && (
        <Paper
          component="section"
          variant="outlined"
          sx={{ bgcolor: "background.default", borderColor: "divider", p: 2, mb: 2 }}
        >
          {toolbar}
        </Paper>
      )}
      {children}
    </Box>
  );
}

export function ManagementTableContainer({ children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ overflowX: "auto", borderColor: "divider", boxShadow: "none" }}
    >
      {children}
    </Paper>
  );
}

export function ManagementTruncatedText({ children }) {
  const text = children == null || children === "" ? "－" : String(children);

  return (
    <Tooltip title={text} describeChild>
      <Box component="span" sx={truncatedTextSx}>
        {text}
      </Box>
    </Tooltip>
  );
}

export function ManagementIconActionButton({ ariaLabel, tooltip, onClick, children }) {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        aria-label={ariaLabel}
        onClick={onClick}
        size="small"
        sx={{
          width: 40,
          height: 40,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          color: "primary.main",
          "&:hover": {
            bgcolor: "action.hover",
            borderColor: "primary.main",
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

export function ManagementListPagination({ count, page, onPageChange }) {
  if (count === 0) {
    return null;
  }

  return (
    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={MANAGEMENT_ROWS_PER_PAGE}
      rowsPerPageOptions={[]}
      labelDisplayedRows={({ from, to, count: total }) => `${from}～${to}件 / 全${total}件`}
      slotProps={{
        actions: {
          nextButton: { "aria-label": "次のページへ" },
          previousButton: { "aria-label": "前のページへ" },
        },
      }}
      sx={{
        borderTop: 1,
        borderColor: "divider",
        ".MuiTablePagination-toolbar": { minHeight: 56, px: { xs: 1, sm: 2 } },
        ".MuiTablePagination-spacer": { flex: "1 1 auto" },
        ".MuiTablePagination-selectLabel, .MuiTablePagination-input": { display: "none" },
      }}
    />
  );
}

export function ManagementEmptyState({ children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: "divider",
        color: "text.secondary",
        py: 6,
        textAlign: "center",
      }}
    >
      <Typography>{children}</Typography>
    </Paper>
  );
}
